import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { 
  User, Clock, MessageSquare, Send, AlertCircle, 
  CheckCircle, XCircle, Calendar, Mail, Phone, Tag, Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';

export default function CustomerTicketDialog({ ticket, open, onOpenChange }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && ticket) {
      loadComments();
    }
  }, [open, ticket]);

  const loadComments = async () => {
    if (!ticket) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/portal/customer/tickets/${ticket.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/portal/customer/tickets/${ticket.id}/comments`, {
        comment_text: newComment
      });
      
      toast.success('Comment added successfully');
      setNewComment('');
      await loadComments();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical':
        return <XCircle className="h-4 w-4" />;
      case 'high':
        return <AlertCircle className="h-4 w-4" />;
      case 'medium':
        return <Clock className="h-4 w-4" />;
      default:
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-purple-100 text-purple-800 border-purple-200';
    }
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatPriority = (priority) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  if (!ticket) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="customer-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{ticket.ticket_number}</span>
            <Badge variant="outline" className={getPriorityColor(ticket.priority)}>
              {getPriorityIcon(ticket.priority)}
              <span className="ml-1">{formatPriority(ticket.priority)}</span>
            </Badge>
            <Badge variant="outline" className={getStatusColor(ticket.status)}>
              {formatStatus(ticket.status)}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Ticket Details */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">{ticket.title}</h3>
                  {ticket.description && (
                    <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{ticket.description}</p>
                  )}
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start gap-2">
                    <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-500">Created</p>
                      <p className="font-medium">{formatInCST(ticket.created_at)}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-500">Last Updated</p>
                      <p className="font-medium">{formatInCST(ticket.updated_at)}</p>
                    </div>
                  </div>

                  {ticket.contact_person && (
                    <div className="flex items-start gap-2">
                      <User className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-gray-500">Contact Person</p>
                        <p className="font-medium">{ticket.contact_person}</p>
                      </div>
                    </div>
                  )}

                  {ticket.contact_email && (
                    <div className="flex items-start gap-2">
                      <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-gray-500">Contact Email</p>
                        <p className="font-medium">{ticket.contact_email}</p>
                      </div>
                    </div>
                  )}

                  {ticket.contact_phone && (
                    <div className="flex items-start gap-2">
                      <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-gray-500">Contact Phone</p>
                        <p className="font-medium">{ticket.contact_phone}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-2">
                    <Tag className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-gray-500">Category</p>
                      <p className="font-medium capitalize">{ticket.category || 'Other'}</p>
                    </div>
                  </div>

                  {ticket.support_type && (
                    <div className="flex items-start gap-2">
                      <Monitor className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-gray-500">Support Type</p>
                        <p className="font-medium capitalize">{ticket.support_type}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversation */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="h-5 w-5 text-gray-600" />
                <h3 className="font-semibold">Conversation</h3>
                <Badge variant="outline" className="ml-auto">
                  {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
                </Badge>
              </div>

              {/* Comments List */}
              <div className="space-y-4 max-h-96 overflow-y-auto mb-4" data-testid="customer-comments-list">
                {loading ? (
                  <p className="text-center text-gray-500 py-4">Loading comments...</p>
                ) : comments.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No comments yet. Start the conversation!</p>
                ) : (
                  comments.map((comment) => (
                    <div 
                      key={comment.id} 
                      className="flex gap-3 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      data-testid={`customer-comment-${comment.id}`}
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
                          {comment.author_name ? comment.author_name.charAt(0).toUpperCase() : 'U'}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{comment.author_name || 'Unknown'}</span>
                          <Badge variant="outline" className="text-xs">
                            {comment.author_role === 'admin' ? 'Staff' : 
                             comment.author_role === 'technician' ? 'Technician' : 'Customer'}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {formatInCST(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-sm mt-2 whitespace-pre-wrap text-gray-700">{comment.comment_text}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <Separator className="my-4" />

              {/* Add Comment */}
              <div className="space-y-3">
                <Textarea
                  placeholder="Type your message here..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={3}
                  className="resize-none"
                  data-testid="customer-comment-input"
                />
                <div className="flex justify-end">
                  <Button 
                    onClick={handleAddComment} 
                    disabled={submitting || !newComment.trim()}
                    data-testid="customer-add-comment-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
